import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../App';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [forgotMode, setForgotMode] = useState(false);
  const [resetEmail, setResetEmail] = useState('');

  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email: resetEmail });
      toast.success(res.data.message);
      setForgotMode(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not send reset email');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, loginData, { withCredentials: true });
      await login(res.data);
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1769986824680-9dcce061dde2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwzfHxjb2ZmZWUlMjBzaG9wJTIwaW50ZXJpb3IlMjBiYXJpc3RhJTIwbGF0dGUlMjBhcnR8ZW58MHx8fHwxNzczODMxNzQwfDA&ixlib=rb-4.1.0&q=85"
          alt="Coffee latte art"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent" />
        <div className="absolute bottom-12 left-12 text-white">
          <h2 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            The Merchant Coffee
          </h2>
          <p className="text-lg text-white/80">TMC Stock App</p>
        </div>
      </div>

      {/* Right side - Auth forms */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md space-y-6 animate-fadeIn">
          {/* Logo/Brand */}
          <div className="text-center">
            <img src="/logo.png" alt="The Merchant - Artisan Coffee & Eatery" className="mx-auto w-64 max-w-full h-auto mb-6" data-testid="login-logo" />
            <h1 className="text-3xl font-bold text-foreground mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              TMC Stock App
            </h1>
            <p className="text-muted-foreground">
              Sign in to manage stock for The Merchant Coffee
            </p>
          </div>

          {forgotMode ? (
            <form onSubmit={handleForgot} className="space-y-4" data-testid="forgot-password-form">
              <p className="text-sm text-muted-foreground">Enter your email and we'll send you a link to reset your password.</p>
              <div className="space-y-2">
                <Label htmlFor="reset-email">Email</Label>
                <Input id="reset-email" type="email" placeholder="you@example.com" required
                  value={resetEmail} onChange={(e) => setResetEmail(e.target.value)}
                  data-testid="reset-email-input" />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="send-reset-btn">
                {loading ? 'Sending...' : 'Send reset link'}
              </Button>
              <button type="button" onClick={() => setForgotMode(false)}
                className="w-full text-sm text-muted-foreground hover:underline" data-testid="back-to-login-btn">
                Back to sign in
              </button>
            </form>
          ) : (
            <form onSubmit={handleEmailLogin} className="space-y-4" data-testid="email-login-form">
              <div className="space-y-2">
                <Label htmlFor="login-email">Email</Label>
                <Input id="login-email" type="email" placeholder="you@example.com" required
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  data-testid="login-email-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="login-password">Password</Label>
                <Input id="login-password" type="password" placeholder="Enter your password" required
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  data-testid="login-password-input" />
              </div>
              <button type="button" onClick={() => { setForgotMode(true); setResetEmail(loginData.email); }}
                className="text-sm text-primary hover:underline" data-testid="forgot-password-link">
                Forgot password?
              </button>
              <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-btn">
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          )}

          {/* Mobile image */}
          <div className="lg:hidden">
            <img
              src="https://images.unsplash.com/photo-1769986824680-9dcce061dde2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwzfHxjb2ZmZWUlMjBzaG9wJTIwaW50ZXJpb3IlMjBiYXJpc3RhJTIwbGF0dGUlMjBhcnR8ZW58MHx8fHwxNzczODMxNzQwfDA&ixlib=rb-4.1.0&q=85"
              alt="Coffee"
              className="w-full h-48 object-cover rounded-lg mt-4"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
